import { useEffect, useRef } from "react";

import type { RefObject } from "react";

function getScrollParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Flags a sticky element with `data-stuck` while it is stuck, so it can be
 * styled with `data-stuck:` variants without re-rendering. The element must be
 * `sticky -top-px`: once stuck, its first pixel row leaves the scroll container
 * and it stops being fully visible.
 */
function useStuckAttribute<T extends HTMLElement>(): RefObject<T | null> {
  const stickyRef = useRef<T>(null);

  useEffect(() => {
    const sticky = stickyRef.current;
    if (!sticky || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const rootTop = entry.rootBounds?.top ?? 0;
          sticky.toggleAttribute(
            "data-stuck",
            entry.intersectionRatio < 1 &&
              entry.boundingClientRect.top < rootTop,
          );
        });
      },
      // The bottom margin keeps the element from counting as cut off at the bottom of the container,
      // so a ratio below 1 only ever means it crossed the top
      {
        root: getScrollParent(sticky),
        rootMargin: "0px 0px 100% 0px",
        threshold: [0, 1],
      },
    );
    observer.observe(sticky);

    return () => observer.disconnect();
  }, []);

  return stickyRef;
}

export default useStuckAttribute;
